<template>
  <div>
    <LoadingView
      :is-loading="isLoading"
      message="Checking contractor compliance"
    />
  </div>
</template>

<script lang="ts" setup>
  import LoadingView from '@/views/LoadingView.vue'
  import { useRouter } from 'vue-router'
  import { ref } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useCustomerStore } from '@/storage/customer'
  import { useUserStore } from '@/storage/user'
  import { useSiteEntryConfirmationStore } from '@/storage/siteEntryConfirmation'
  import { getSiteEntryCheck } from '@/components/use/siteEntryConfirmation'
  import {
    ContractorEntityDocumentRequirement,
    getContractorEntityDocumentRequirements,
    storeFailedContractorCompliance,
  } from '@/services/api/contractorCompliance'
  import { useSiteStore } from '@/storage/site'
  import ReportableException from '@/exceptions/ReportableException'
  import utc from 'dayjs/plugin/utc'
  import timezone from 'dayjs/plugin/timezone'
  import dayjs from 'dayjs'

  dayjs.extend(utc)
  dayjs.extend(timezone)

  const { id: customerId } = storeToRefs(useCustomerStore())
  const { selectedContractorCompanyId } = storeToRefs(useUserStore())
  const { id: siteId } = storeToRefs(useSiteStore())
  const { removeSiteEntryCheck, addSiteEntryCheck } =
    useSiteEntryConfirmationStore()

  const router = useRouter()
  const isLoading = ref(false)

  isLoading.value = true
  await checkContractorCompliance()
  await routeToNext()
  isLoading.value = false

  async function checkContractorCompliance(): Promise<void> {
    const requirements = await getContractorEntityDocumentRequirements(
      [customerId.value],
      selectedContractorCompanyId.value,
    )

    handleSiteEntryChecks(requirements)
  }

  function hasValidDocument(
    requirement: ContractorEntityDocumentRequirement,
  ): boolean {
    const now = new Date()

    // requiredBy and expiresAt are assumed to be in UTC - convert and compare
    const requiredByConverted = dayjs(requirement.requiredBy)
      .tz(dayjs.tz.guess())
      .toDate()

    // Grace window: before the required-by date the contractor is compliant
    // regardless of whether a document has been submitted yet.
    if (requiredByConverted > now) {
      return true
    }

    return requirement.contractorEntityDocuments.some(
      (doc) =>
        doc.status === 'APPROVED' &&
        doc.expiresAt !== null &&
        dayjs(doc.expiresAt).tz(dayjs.tz.guess()).toDate() > now,
    )
  }

  function handleSiteEntryChecks(
    requirements: ContractorEntityDocumentRequirement[],
  ): void {
    const existingCheck = getSiteEntryCheck('Contractor Compliance')

    if (existingCheck !== undefined) {
      removeSiteEntryCheck(existingCheck)
    }

    if (requirements.length === 0) {
      addSiteEntryCheck({
        canContinue: true,
        checks: [],
        passed: true,
        title: 'Contractor Compliance',
      })
      return
    }

    const failingRequirements = requirements.filter(
      (requirement) => !hasValidDocument(requirement),
    )
    const passed = failingRequirements.length === 0

    addSiteEntryCheck({
      canContinue: passed,
      passed,
      title: 'Contractor Compliance',
    })

    if (!passed) {
      storeFailedContractorCompliance(
        customerId.value,
        siteId.value,
        selectedContractorCompanyId.value,
      ).catch((error) => {
        const message =
          error instanceof Error
            ? error.message
            : 'Store failed contractor compliance failed'
        throw new ReportableException(message, { error })
      })
    }
  }

  async function routeToNext(): Promise<void> {
    if (!useSiteEntryConfirmationStore().canContinue) {
      await router.replace({ name: 'siteEntryConfirmation' })
    } else {
      await router.replace({ name: 'checkAccreditation' })
    }
  }
</script>
